/**
 * Unit commands panel — renders the command catalog the backend
 * returns from GET /sites/{id}/available-commands.
 *
 * The backend is the single source of truth for which commands are
 * available; this component renders sections and lets the user build
 * a queue. Commands aren't dispatched on click — clicking adds the
 * command (after collecting any params or confirmation) to a pending
 * list. The user reviews the list and hits "Send" to fire them all
 * at once via POST /sites/{id}/commands/dispatch/.
 *
 * Bespoke commands declared with `params: "custom"` are not queueable;
 * the panel emits onCustomCommand and the parent decides what to do
 * (typically: open the legacy bespoke dialog).
 */

// Icon imports — the catalog returns string `icon` hints; this map
// resolves them to actual MUI icon components. Unknown values fall
// through to PlayArrow.
import Bolt from '@mui/icons-material/Bolt';
import CellTower from '@mui/icons-material/CellTower';
import DataObject from '@mui/icons-material/DataObject';
import Delete from '@mui/icons-material/Delete';
import Dns from '@mui/icons-material/Dns';
import ElectricBolt from '@mui/icons-material/ElectricBolt';
import GridView from '@mui/icons-material/GridView';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import Lightbulb from '@mui/icons-material/Lightbulb';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import Lock from '@mui/icons-material/Lock';
import LockOpen from '@mui/icons-material/LockOpen';
import Notifications from '@mui/icons-material/Notifications';
import NotificationsOff from '@mui/icons-material/NotificationsOff';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import PlayArrow from '@mui/icons-material/PlayArrow';
import PowerOff from '@mui/icons-material/PowerOff';
import PowerSettingsNew from '@mui/icons-material/PowerSettingsNew';
import RestartAlt from '@mui/icons-material/RestartAlt';
import SatelliteAlt from '@mui/icons-material/SatelliteAlt';
import Schedule from '@mui/icons-material/Schedule';
import SettingsBackupRestore from '@mui/icons-material/SettingsBackupRestore';
import SettingsEthernet from '@mui/icons-material/SettingsEthernet';
import Sync from '@mui/icons-material/Sync';
import Tune from '@mui/icons-material/Tune';
import Upgrade from '@mui/icons-material/Upgrade';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- sanctioned: persist+action toast unsupported by addToast (custom action button + closeSnackbar handle)
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';

import { useUser } from '@sophocles/app';
import Ipv4Field from '@sophocles/components/FormFields/Ipv4Field';
import { loadUnitCommands } from '@sophocles/features/commands/api';
import { invalidateAvailableCommands, selectAvailableCommands } from '@sophocles/features/commands/slice';
import { selectUnitModels } from '@sophocles/features/reference';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';
import { isValidIpv4 } from '@sophocles/utils/commandUtils';
import {
  buildModelNameResolver,
  incompatibleFirmwareModelRows,
} from '@sophocles/views/admin/engineering/firmwareUpgrades/firmwareUpgradeHelpers';
import FirmwareVersionAutocomplete from '@sophocles/views/admin/engineering/firmwareUpgrades/FirmwareVersionAutocomplete';

const NOOP = () => {};

const ICONS = {
  satellite_alt: SatelliteAlt,
  cell_tower: CellTower,
  schedule: Schedule,
  bolt: Bolt,
  electric_bolt: ElectricBolt,
  grid_view: GridView,
  data_object: DataObject,
  dns: Dns,
  power_settings_new: PowerSettingsNew,
  power_off: PowerOff,
  restart_alt: RestartAlt,
  lightbulb: Lightbulb,
  lightbulb_outline: LightbulbOutlined,
  lightbulb_outlined: LightbulbOutlined,
  lock: Lock,
  lock_open: LockOpen,
  notifications_off: NotificationsOff,
  notifications: Notifications,
  tune: Tune,
  photo_camera: PhotoCamera,
  hourglass_empty: HourglassEmpty,
  settings_ethernet: SettingsEthernet,
  sync: Sync,
  settings_backup_restore: SettingsBackupRestore,
  upgrade: Upgrade,
};

function CommandIcon({ icon }) {
  const Component = ICONS[icon] || PlayArrow;
  return <Component sx={{ fontSize: 22, color: 'text.secondary' }} />;
}

// Privilege badge rendered next to a command when the backend marks
// it as gated above the ordinary-user bar. ``tier="eng"`` flags
// engineering-only test surfaces (STATUS_GET_SHOTGUN, FACTORY_RESET);
// ``tier="staff"`` flags privileged-perm / staff-only diagnostics
// and config commands. The actual gate is server-side — this chip
// just tells the operator "you're seeing this because of your role".
function CommandTierChip({ tier, t }) {
  if (tier !== 'eng' && tier !== 'staff') return null;
  const isEng = tier === 'eng';
  return (
    <Chip
      size="small"
      variant="outlined"
      color={isEng ? 'warning' : 'info'}
      label={t(isEng ? 'commandCatalog.tierEng' : 'commandCatalog.tierStaff', {
        defaultValue: isEng ? 'ENG' : 'Staff',
      })}
      sx={{ ml: 1, height: 20, fontSize: '0.6875rem', letterSpacing: 0.5 }}
    />
  );
}

function ParamDialog({ open, command, t, onClose, onConfirm, relays, submitting, unitModelId }) {
  const [values, setValues] = useState({});
  // Major-version filter for the firmware picker: default the picker
  // to the unit's current major release so an
  // operator can't accidentally cross-grade between, say, 9.x → 10.x.
  // The danger toggle below the picker lifts the filter when an
  // engineer genuinely needs to see other majors.
  const [restrictMajor, setRestrictMajor] = useState(true);
  const firmwareVersionsById = useSelector(state => state.entities.relayControllerFirmwareVersions);

  useEffect(() => {
    // Pre-fill any param defaults from the catalog so required choices
    // (e.g. upgrade_type) start populated and Submit isn't disabled
    // until the user touches the field.
    const seeds = {};
    const params = Array.isArray(command?.params) ? command.params : [];
    for (const p of params) {
      if (p.default !== undefined && p.default !== null) {
        seeds[p.name] = p.default;
      }
    }
    setValues(seeds);
    setRestrictMajor(true);
  }, [command]);

  // Firmware autocomplete needs the full option object for matching;
  // we only persist the id in `values` so consumers get a clean
  // primitive payload. Look the option up by id at render time.
  const resolveFirmwareOption = id => {
    if (id == null || !firmwareVersionsById) return null;
    const match = firmwareVersionsById.get(String(id)) || firmwareVersionsById.get(id);
    return match ? match.toJS() : null;
  };

  if (!command) return null;

  const params = Array.isArray(command.params) ? command.params : [];
  const allFilled = params.every(p => !p.required || values[p.name] !== undefined);
  const allWellFormed = params.every(p => p.type !== 'ipv4' || !values[p.name] || isValidIpv4(values[p.name]));
  const confirmLabel =
    command.key === 'FIRMWARE_UPGRADE'
      ? t('commandCatalog.startUpgrade', { defaultValue: 'Start upgrade' })
      : t('commandCatalog.addToQueue');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t(command.label_key)}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <DialogContentText>{t(command.description_key)}</DialogContentText>
          {params.map(param => {
            if (param.type === 'relay_picker') {
              // `relays` is normalized upstream to [{value, label}] so a
              // paired pair like 1+2 shows as a single option labeled
              // "Relay 1+2" with `value: 1` (the primary; the unit
              // handles the pair).
              const options = (relays || []).map(r =>
                typeof r === 'object' && r !== null
                  ? { value: r.value, label: r.label }
                  : { value: r, label: t('commandCatalog.relayN', { n: r }) },
              );
              return (
                <Select
                  key={param.name}
                  value={values[param.name] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [param.name]: e.target.value }))}
                  displayEmpty
                  hiddenLabel
                  fullWidth
                  size="small"
                >
                  <MenuItem value="" disabled>
                    {t('commandCatalog.selectRelay')}
                  </MenuItem>
                  {options.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              );
            }
            if (param.type === 'number') {
              return (
                <TextField
                  key={param.name}
                  label={param.name}
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{ min: param.min, max: param.max }}
                  value={values[param.name] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [param.name]: Number(e.target.value) }))}
                />
              );
            }
            if (param.type === 'firmware_version_picker') {
              // Backend ships these as hints on the picker descriptor
              // when the unit's `firmware_version` is known. When
              // `current_major` is null (unit hasn't reported), the
              // filter + toggle hide themselves and the autocomplete
              // shows everything — same as before round 2.
              const currentVersion = param.current_version ?? null;
              const currentMajor = param.current_major ?? null;
              return (
                <Stack key={param.name} spacing={1}>
                  {currentVersion ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('commandCatalog.currentFirmware', {
                        version: currentVersion,
                        defaultValue: 'Current: v{{version}}',
                      })}
                    </Typography>
                  ) : null}
                  <FirmwareVersionAutocomplete
                    value={resolveFirmwareOption(values[param.name])}
                    onChange={fv => setValues(v => ({ ...v, [param.name]: fv?.id ?? undefined }))}
                    label={t('commandCatalog.selectFirmwareVersion', { defaultValue: 'Firmware version' })}
                    currentMajor={currentMajor}
                    restrictMajor={restrictMajor}
                    modelId={unitModelId}
                  />
                  {currentMajor != null ? (
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={!restrictMajor}
                          onChange={e => setRestrictMajor(!e.target.checked)}
                          color="warning"
                        />
                      }
                      label={
                        <Typography variant="caption" color="text.secondary">
                          {t('commandCatalog.showAllMajors', {
                            defaultValue: 'Show versions of other major releases (danger)',
                          })}
                        </Typography>
                      }
                    />
                  ) : null}
                </Stack>
              );
            }
            if (param.type === 'ipv4') {
              const typed = values[param.name] ?? '';
              const malformed = typed !== '' && !isValidIpv4(typed);
              return (
                <Ipv4Field
                  key={param.name}
                  label={param.name}
                  value={typed}
                  error={malformed}
                  helperText={
                    malformed
                      ? t('commandCatalog.invalidIpv4', {
                          defaultValue: 'Enter an address like 10.20.30.40',
                        })
                      : ' '
                  }
                  onChange={next => setValues(v => ({ ...v, [param.name]: next }))}
                />
              );
            }
            if (param.type === 'text') {
              return (
                <TextField
                  key={param.name}
                  label={param.name}
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  value={values[param.name] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [param.name]: e.target.value }))}
                  // Filled multiline centers the textarea within the InputBase root by
                  // default; anchor it to the top so short text lands on line 1 instead
                  // of floating mid-slot.
                  sx={{
                    '& .MuiInputBase-root.MuiInputBase-multiline': { alignItems: 'flex-start' },
                    '& .MuiInputBase-inputMultiline': { alignSelf: 'flex-start', padding: '0 !important' },
                  }}
                />
              );
            }
            if (param.type === 'string_choice') {
              const opts = Array.isArray(param.options) ? param.options : [];
              return (
                <Select
                  key={param.name}
                  value={values[param.name] ?? ''}
                  onChange={e => setValues(v => ({ ...v, [param.name]: e.target.value }))}
                  displayEmpty
                  hiddenLabel
                  fullWidth
                  size="small"
                >
                  <MenuItem value="" disabled>
                    {param.name}
                  </MenuItem>
                  {opts.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label ?? opt.value}
                    </MenuItem>
                  ))}
                </Select>
              );
            }
            return null;
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!allFilled || !allWellFormed || submitting}
          onClick={() => onConfirm(values)}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ConfirmDialog({ open, command, t, onClose, onConfirm }) {
  if (!command) return null;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t(command.label_key)}</DialogTitle>
      <DialogContent>
        <DialogContentText>{t(command.description_key)}</DialogContentText>
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('commandCatalog.confirmWarning')}
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('actions.cancel')}</Button>
        <Button variant="contained" color="warning" onClick={onConfirm}>
          {t('commandCatalog.addToQueue')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// A model-compatibility refusal names every unit the image wasn't built
// for. The server's own sentence leads; a line per unit follows so the
// operator can see which hardware it actually is.
function formatIncompatibleModelRefusal(body, t, resolveModelName) {
  const rows = incompatibleFirmwareModelRows(body, resolveModelName);
  const lead =
    body?.detail ??
    t('commandCatalog.errors.incompatibleFirmwareModel', {
      defaultValue: 'This firmware was not built for this unit\u2019s hardware model.',
    });
  if (rows.length === 0) return lead;
  const lines = rows.map(row =>
    t('commandCatalog.errors.incompatibleFirmwareModelUnit', {
      unitId: row.unitId,
      siteId: row.siteId ?? '\u2014',
      unitModel: row.unitModel,
      firmwareModels: row.firmwareModels || '\u2014',
      defaultValue: 'Unit {{unitId}} (site {{siteId}}) is a {{unitModel}}; this firmware is for {{firmwareModels}}.',
    }),
  );
  return `${lead}\n\n${lines.map(line => `\u2022 ${line}`).join('\n')}`;
}

function paramSummary(params) {
  if (!params) return '';
  return Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
}

export default function UnitCommandsPanel({
  siteId,
  unitId,
  unitModelId = null,
  relays = [],
  onCustomCommand = NOOP,
  onCommandSent = NOOP,
  onClose = NOOP,
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const dispatch = useDispatch();
  // Catalog is cached in the commands slice keyed by site, so reopening the
  // dialog reuses it instead of re-hitting /available-commands every time.
  const catalogEntry = useSelector(state => selectAvailableCommands(state, siteId));
  const catalog = catalogEntry?.data ?? null;
  const loadError = catalogEntry?.error ?? null;
  const loading = catalogEntry ? Boolean(catalogEntry.loading) : Boolean(siteId);
  const [confirming, setConfirming] = useState(null);
  const [collecting, setCollecting] = useState(null);
  const { dispatchCommands, submitting, activityLocked } = useCommandDispatch(siteId);
  const [feedback, setFeedback] = useState(null);
  const [queue, setQueue] = useState([]); // [{ id, command, params }]
  const [collectingSubmitting, setCollectingSubmitting] = useState(false);
  const unitModels = useSelector(selectUnitModels);
  const resolveModelName = useMemo(() => buildModelNameResolver(unitModels), [unitModels]);

  const reload = useCallback(() => {
    if (!siteId) return;
    dispatch(invalidateAvailableCommands(siteId));
    dispatch(loadUnitCommands(siteId));
  }, [dispatch, siteId]);

  useEffect(() => {
    if (siteId) dispatch(loadUnitCommands(siteId));
  }, [dispatch, siteId]);

  const enqueue = useCallback((command, params = {}) => {
    setQueue(q => [...q, { id: `${command.key}:${Date.now()}:${Math.random()}`, command, params }]);
    setConfirming(null);
    setCollecting(null);
  }, []);

  const removeFromQueue = useCallback(id => {
    setQueue(q => q.filter(entry => entry.id !== id));
  }, []);

  const handleClick = useCallback(
    command => {
      // ``params: "custom"`` means the backend autofills the payload
      // from unit / site state at dispatch time (current time for
      // TIME_SET, current config for NETWORK_SET, etc.). Treat those
      // the same as no-params commands and enqueue with an empty
      // payload — the dispatch endpoint passes the empty params
      // through opaquely and the protocol layer fills in the rest.
      if (Array.isArray(command.params) && command.params.length > 0) {
        setCollecting(command);
        return;
      }
      if (command.confirmation) {
        setConfirming(command);
        return;
      }
      enqueue(command);
    },
    [enqueue],
  );

  const sendQueue = useCallback(async () => {
    if (queue.length === 0 || activityLocked) return;
    setFeedback(null);
    const payload = queue.map(({ command, params }) => ({ key: command.key, params }));
    const { status, pendingCommands, failures, messages, errorBody } = await dispatchCommands(payload);

    // Firmware upgrades don't produce a PendingUnitCommand row, so
    // the usual pending/completed-command toast pipeline never fires
    // for them. Surface a dedicated snackbar per dispatched batch so
    // the operator gets immediate "dispatched, will begin shortly"
    // feedback with a deep-link to the progress page.
    for (const row of pendingCommands) {
      const batchId = row?.firmware_batch_id;
      if (!batchId) continue;
      enqueueSnackbar(
        t('commandCatalog.upgradeDispatched', {
          defaultValue: 'Firmware upgrade dispatched — will begin shortly',
        }),
        {
          variant: 'success',
          action: snackbarKey => (
            <Button
              size="small"
              color="inherit"
              component={RouterLink}
              to={`/staff/firmware-upgrades/${batchId}`}
              onClick={() => closeSnackbar(snackbarKey)}
            >
              {t('commandCatalog.viewProgress', { defaultValue: 'View progress' })}
            </Button>
          ),
        },
      );
    }

    if (status === DISPATCH_STATUS.SUCCESS) {
      setQueue([]);
      onCommandSent(payload, { pending_commands: pendingCommands, errors: failures });
      // A dispatched command (firmware upgrade, reset, …) can change what's
      // available next time, so drop the cached catalog for this site.
      dispatch(invalidateAvailableCommands(siteId));
      // Full success — close the dialog. Skip the success Alert
      // since the dialog is going away anyway; the parent decides
      // whether to surface a confirmation toast.
      onClose();
    } else if (status === DISPATCH_STATUS.PARTIAL) {
      // Partial: keep the failed entries in the queue so the user
      // can fix and retry.
      const failedIndexes = new Set(failures.map(e => e.index));
      setQueue(q => q.filter((_, idx) => failedIndexes.has(idx)));
      const first = failures[0];
      setFeedback({
        severity: 'warning',
        message: t('commandCatalog.partialSent', {
          sent: pendingCommands.length,
          failed: failures.length,
          firstError: first.message_key ? t(first.message_key) : first.detail,
        }),
      });
      onCommandSent(payload, { pending_commands: pendingCommands, errors: failures });
    } else {
      // Failed: nothing queued — an in-body per-command failure or a thrown
      // transport / HTTP 400 already parsed into human-readable messages.
      const first = failures[0];
      const message =
        (errorBody?.code === 'incompatible_firmware_model'
          ? formatIncompatibleModelRefusal(errorBody, t, resolveModelName)
          : null) ??
        messages[0] ??
        (first ? (first.message_key ? t(first.message_key) : first.detail) : null) ??
        t('commandCatalog.sendError');
      setFeedback({ severity: 'error', message: String(message) });
    }
  }, [
    queue,
    activityLocked,
    siteId,
    t,
    onCommandSent,
    onClose,
    enqueueSnackbar,
    closeSnackbar,
    dispatch,
    dispatchCommands,
    resolveModelName,
  ]);

  const sections = catalog?.sections ?? [];
  const currentUpgrade = catalog?.current_upgrade ?? null;
  const { permissions } = useUser();
  const canViewUpgrade = permissions.canUpgradeFirmware;
  const upgradeProgressPct = useMemo(() => {
    if (!currentUpgrade) return 0;
    const end = currentUpgrade.end_segment || 0;
    if (end <= 0) return 0;
    return Math.round(((currentUpgrade.programming_segment || 0) / end) * 100);
  }, [currentUpgrade]);
  // Defense in depth: if an upgrade lands mid-session, drop any queued
  // commands so the user can't try to send them after the lockout
  // banner appears.
  useEffect(() => {
    if (currentUpgrade && queue.length > 0) {
      setQueue([]);
    }
  }, [currentUpgrade, queue.length]);
  const isEmpty = useMemo(
    () => !loading && !currentUpgrade && sections.length === 0,
    [loading, currentUpgrade, sections.length],
  );

  if (loading && !catalog) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={reload}>
            {t('actions.retry')}
          </Button>
        }
      >
        {t('commandCatalog.loadError')}
      </Alert>
    );
  }

  // Header used by both the desktop sidebar and the mobile chip strip.
  const queueHeader = (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="overline" color="text.secondary">
        {t('commandCatalog.pendingQueue', { count: queue.length })}
      </Typography>
      {queue.length > 0 && (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            size="small"
            color="inherit"
            onClick={() => setQueue([])}
            disabled={submitting}
            startIcon={<Delete fontSize="small" />}
          >
            {t('commandCatalog.clearQueue')}
          </Button>
        </Box>
      )}
    </Stack>
  );

  const sendButton = (
    <Button
      variant="contained"
      color="primary"
      onClick={sendQueue}
      disabled={queue.length === 0 || submitting || activityLocked}
      startIcon={submitting ? <CircularProgress size={16} /> : null}
      fullWidth={isDesktop}
    >
      {submitting ? t('commandCatalog.sending') : t('commandCatalog.sendN', { count: queue.length })}
    </Button>
  );

  // Mobile: chip strip at top, Send button at the very bottom.
  const mobileQueueStrip = (
    <Box
      sx={{
        mb: 2,
        p: 1.5,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 'var(--radius-md)',
        minHeight: 56,
      }}
    >
      <Box sx={{ mb: queue.length ? 1 : 0 }}>{queueHeader}</Box>
      {queue.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          {t('commandCatalog.queueEmptyHint')}
        </Typography>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {queue.map(entry => {
            const summary = paramSummary(entry.params);
            const label = summary ? `${t(entry.command.label_key)} (${summary})` : t(entry.command.label_key);
            return (
              <Chip
                key={entry.id}
                label={label}
                onDelete={submitting ? undefined : () => removeFromQueue(entry.id)}
                icon={<CommandIcon icon={entry.command.icon} />}
              />
            );
          })}
        </Stack>
      )}
    </Box>
  );

  // Desktop: sticky right-hand sidebar with a vertical list of queued
  // entries. Sticky lives on the column wrapper (see render below)
  // so the sidebar pins relative to the scrolling DialogContent
  // rather than being trapped at its column's intrinsic height.
  const desktopQueueSidebar = (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 200px)',
      }}
    >
      <Box sx={{ mb: 1 }}>{queueHeader}</Box>
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 60 }}>
        {queue.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', py: 3 }}>
            {t('commandCatalog.queueEmptyHint')}
          </Typography>
        ) : (
          <List disablePadding dense>
            {queue.map(entry => {
              const summary = paramSummary(entry.params);
              return (
                <ListItem
                  key={entry.id}
                  disableGutters
                  sx={{ pr: 4 }}
                  secondaryAction={
                    <IconButton
                      size="small"
                      edge="end"
                      aria-label="remove"
                      disabled={submitting}
                      onClick={() => removeFromQueue(entry.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CommandIcon icon={entry.command.icon} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(entry.command.label_key)}
                    secondary={summary || null}
                    primaryTypographyProps={{ variant: 'body2' }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
      <Divider sx={{ my: 1 }} />
      {sendButton}
    </Paper>
  );

  const upgradeNotice = currentUpgrade ? (
    <Alert
      severity="info"
      icon={<Upgrade fontSize="small" />}
      action={
        canViewUpgrade ? (
          <Button
            color="inherit"
            size="small"
            component={RouterLink}
            to={`/staff/firmware-upgrades/${currentUpgrade.batch_id}`}
          >
            {t('commandCatalog.viewProgress', { defaultValue: 'View progress' })}
          </Button>
        ) : null
      }
    >
      {t('commandCatalog.upgradeInProgress', {
        pct: upgradeProgressPct,
        version: currentUpgrade.firmware_version,
        defaultValue: 'Firmware upgrade in progress ({{pct}}%) — commands disabled',
      })}
    </Alert>
  ) : null;

  const catalogList = (
    <>
      {upgradeNotice}
      {isEmpty && <Alert severity="info">{t('commandCatalog.noneAvailable')}</Alert>}
      <Stack spacing={2}>
        {sections.map(section => (
          <Box key={section.key}>
            <Typography variant="subtitle2" sx={{ pl: 1, pt: 0.5, pb: 0.5, color: 'text.secondary' }}>
              {t(section.label_key)}
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <List disablePadding dense>
              {section.commands.map(command => (
                <ListItemButton
                  key={command.key}
                  onClick={() => handleClick(command)}
                  disabled={submitting || activityLocked}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CommandIcon icon={command.icon} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <span>{t(command.label_key)}</span>
                        <CommandTierChip tier={command.tier} t={t} />
                      </Box>
                    }
                    secondary={t(command.description_key)}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
        ))}
      </Stack>
    </>
  );

  return (
    <Box>
      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
          {feedback.message}
        </Alert>
      )}

      {isDesktop ? (
        // Desktop: catalog left, sticky queue sidebar right. Sticky
        // is set on the column wrapper so the sidebar pins to the
        // top of DialogContent's scroll area instead of scrolling
        // away with the catalog.
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{catalogList}</Box>
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
              position: 'sticky',
              top: 0,
              alignSelf: 'flex-start',
            }}
          >
            {desktopQueueSidebar}
          </Box>
        </Box>
      ) : (
        // Mobile: queue chip strip on top, catalog below, Send sticky bottom.
        <>
          {mobileQueueStrip}
          {catalogList}
          <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.paper', pt: 2, mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {sendButton}
            </Stack>
          </Box>
        </>
      )}

      <ConfirmDialog
        open={!!confirming}
        command={confirming}
        t={t}
        onClose={() => setConfirming(null)}
        onConfirm={() => enqueue(confirming)}
      />
      <ParamDialog
        open={!!collecting}
        command={collecting}
        t={t}
        relays={relays}
        unitModelId={unitModelId}
        submitting={collectingSubmitting}
        onClose={() => {
          if (collectingSubmitting) return;
          setCollecting(null);
        }}
        onConfirm={values => {
          // Firmware-upgrade entries flow through the standard dispatch
          // queue now — the dispatch_commands view recognizes the
          // FIRMWARE_UPGRADE key and calls start_upgrade_batch on the
          // server. The user clicks the panel's Send button to fire.
          enqueue(collecting, values);
        }}
      />
    </Box>
  );
}
